import { Chess } from 'chess.js';

/**
 * PGN Parser
 * Single Responsibility: Turning PGN text (several games, comments, variations)
 * into flat training lines: { name, pgn, moves, comments }
 *
 * Comments are keyed by the number of moves played when they apply:
 * key 0 is shown before the first move, key N after the N-th move.
 */

const TOKEN_REGEX = /\{[^}]*\}|;[^\n]*|\[[^\]]*\]|\(|\)|\$\d+|\d+\.(?:\.\.)?|1-0|0-1|1\/2-1\/2|\*|[^\s(){};[\]]+/g;

const splitGames = (text) => {
  const games = [];
  let current = [];
  let inMoves = false;
  text.replace(/\r/g, '').split('\n').forEach(rawLine => {
    const line = rawLine.trim();
    if (line.startsWith('[') && inMoves) {
      games.push(current.join('\n'));
      current = [];
      inMoves = false;
    }
    if (line && !line.startsWith('[')) inMoves = true;
    current.push(rawLine);
  });
  if (current.join('').trim()) games.push(current.join('\n'));
  return games;
};

const parseHeaders = (gameText) => {
  const headers = {};
  gameText.replace(/\[(\w+)\s+"([^"]*)"\]/g, (_, key, value) => {
    headers[key] = value;
    return '';
  });
  return headers;
};

// Builds a tree: a sequence is a list of moves, each move may carry
// alternative sequences (variations) that replace it.
const parseSequence = (tokens, state) => {
  const sequence = { preComment: '', items: [] };
  while (state.index < tokens.length) {
    const token = tokens[state.index++];
    if (token === ')') return sequence;
    if (token === '(') {
      const variation = parseSequence(tokens, state);
      const last = sequence.items[sequence.items.length - 1];
      if (last) last.variations.push(variation);
      continue;
    }
    if (token.startsWith('{')) {
      const comment = token.slice(1, -1).replace(/\[%[^\]]*\]/g, '').trim();
      if (!comment) continue;
      const last = sequence.items[sequence.items.length - 1];
      if (last) last.comment = [last.comment, comment].filter(Boolean).join(' ');
      else sequence.preComment = [sequence.preComment, comment].filter(Boolean).join(' ');
      continue;
    }
    if (token.startsWith(';') || token.startsWith('[') || token.startsWith('$')) continue;
    if (/^\d+\.(\.\.)?$/.test(token) || /^(1-0|0-1|1\/2-1\/2|\*)$/.test(token)) continue;
    const san = token.replace(/^\d+\.(\.\.)?/, '').replace(/[!?]+$/, '');
    if (san) sequence.items.push({ san, comment: '', variations: [] });
  }
  return sequence;
};

// Expands the tree into every path from the root to a leaf (main line first)
const expand = (sequence, base) => {
  const results = [];
  const path = { moves: [...base.moves], comments: { ...base.comments } };
  if (sequence.preComment) path.comments[path.moves.length] = sequence.preComment;
  sequence.items.forEach(item => {
    item.variations.forEach(variation => {
      results.push(...expand(variation, { moves: [...path.moves], comments: { ...path.comments } }));
    });
    path.moves.push(item.san);
    if (item.comment) path.comments[path.moves.length] = item.comment;
  });
  return [path, ...results];
};

// Replays moves with chess.js to validate them and normalise the SAN
const validate = (moves) => {
  const game = new Chess();
  const sanMoves = [];
  for (const move of moves) {
    try {
      sanMoves.push(game.move(move).san);
    } catch (error) {
      return { moves: sanMoves, error: `Illegal move "${move}" after ${formatPgn(sanMoves) || 'the start'}` };
    }
  }
  return { moves: sanMoves, error: null };
};

export const formatPgn = (moves) => moves
  .map((move, index) => (index % 2 === 0 ? `${index / 2 + 1}. ${move}` : move))
  .join(' ');

/**
 * Parses PGN text into training lines.
 * @returns {{ lines: Array, errors: Array<string> }}
 */
export const parsePgnToLines = (text) => {
  const lines = [];
  const errors = [];
  const seen = new Set();

  splitGames(text || '').forEach((gameText, gameIndex) => {
    const headers = parseHeaders(gameText);
    const moveText = gameText.replace(/^\s*\[[^\]]*\]\s*$/gm, '');
    const tokens = moveText.match(TOKEN_REGEX) || [];
    const tree = parseSequence(tokens, { index: 0 });
    const paths = expand(tree, { moves: [], comments: {} }).filter(path => path.moves.length > 0);
    const baseName = headers.ChapterName
      || (headers.Event && headers.Event !== '?' ? headers.Event.replace(/^.*?:\s*/, '') : '')
      || headers.Opening
      || `Game ${gameIndex + 1}`;

    paths.forEach((path, pathIndex) => {
      const { moves, error } = validate(path.moves);
      if (error) {
        errors.push(`${baseName}: ${error}`);
        return;
      }
      const pgn = formatPgn(moves);
      if (seen.has(pgn)) return;
      seen.add(pgn);
      const comments = Object.fromEntries(
        Object.entries(path.comments).filter(([key]) => Number(key) <= moves.length)
      );
      lines.push({
        name: paths.length > 1 ? `${baseName} #${pathIndex + 1}` : baseName,
        pgn,
        moves,
        comments
      });
    });
  });

  return { lines, errors };
};
